import queryString from 'query-string-es5';
import React from 'react';
import * as ReactRouterDom from 'react-router-dom';
import { Fun, mk_widget, Options, Unit, Widget } from '../main';
import { option } from '../widget';

/**
 * A wrapper over an untyped `React.Route` that adds a type `R` to the captured parameters of the route
 */
export interface Route<R> {
  /**
   * The title to show in the browser's tab, when the route matches.
   *
   * If not given defaults to empty string.
  */
  title?:string,

  /** The underlying URL path-template of the untyped `React.Route`.
   *
   * - It may be a hardcoded (relative)url path.
   * - It may include the HTTP query parameters.
   * - It can capture parameters.
   *
   * Note: all templates use `exact` matching and no partial matching.
   *
   * Template URL examples:
   * - `/sometext`  , Exact matching of the string
   * - `/static/:foo/more` , Captures to the `foo` variable a url segment as string. This string will never contain forward slashes (i.e. single url segment). String can be empty.
   * - `/:foo/:bar?/static` , An optional url segment, the leading slash in front of `/:bar?` is optional in case `:bar` is empty.
   * - `/:segs*` , 0 or more url segments captured in a single `segs` string-variable. The string may contain slashes, thus can be turned into an array of segments with `segs.splt('/')`
   * - `/:segs(\\d+-[^/]*)+` , A variable followed by parentheses is a named regex. In this case, it matches 1 or more segments as in the example url: `1-somedecr1ption/6491237-othEr_Description/...`
   *
   * For more info on route templates, see the official repository: <https://github.com/pillarjs/path-to-regexp>
   */
  path?:string,

  /**
   * A function that gets as input the untyped captured parameters returned the React-Router parser,
   * and returns an equivalent but typed representation of the parameters, typed by `R`.
   */
  paramsToRoute: Fun<any, R>
}

/**
 * A collection of routes that have the same parameters.
 */
export type Routes<R> = Array<Route<R>>

/**
 * A helper function to create a typed-[[Route]] with no title.
 */
export const route = <P,R>(path:string, paramsToRoute:Fun<P,R>) : Route<R> =>
    ({ path:path, paramsToRoute:paramsToRoute })


/**
 * A helper function to construct a typed [[Route]] which has no path and no parameters.
 * It acts as a **catch-all** (default) router.
 */
export const notFoundRouteCase = function<R>(makeRoute:Fun<Unit,R>) : Route<R> {
  return { paramsToRoute:makeRoute }
}

/**
 * An adapter from an array of routes `R` into a widget `R`
 * @deprecated Use instead [[doRouting]], which combines [[routerSwitch]] and [[browserRouter]]
 * Note: There is no support for "nested routing". All hereby supllied routes act at top-level.
 */
export function routerSwitch<R>(options?:Options) : (routes:Routes<R>) => Widget<R> {
  return (routes:Routes<R>) : Widget<R> => mk_widget({ run:onDone =>
    <RouteForwarder routes={routes} onDone={onDone} options={options} key={option("key", options)} />
  })
}

/** A `browserRouter` decorates a widget with a `ReactRouterDom.BrowserRouter` top-level component.
 * @param w is a widget, and *normally* ths should contain a routerswitch
 * @deprecated Use instead [[doRouting]] , which combines [[routerSwitch]] and [[browserRouter]]
 */
export function browserRouter<O>(options?:Options) : (w:Widget<O>) => Widget<O> {
  return (w:Widget<O>) : Widget<O> => mk_widget({ run:onDone =>
    <ReactRouterDom.BrowserRouter key={option("key", options)}>
      {
        w.run(onDone)
      }
    </ReactRouterDom.BrowserRouter>
  })
}

/**
 * Given a collection of [[Route]]s, this Widget outputs the captured 
 * typed-parameters of the route that the user is currently navigating in the browser.
 *
 * Note: There is no support for "nested routing". All hereby supllied routes act at top-level.
 */
export function doRouting<R>(options?:Options) : (routes:Routes<R>) => Widget<R> {
  return (routes:Routes<R>) : Widget<R> => mk_widget({ run:onDone =>
    <ReactRouterDom.BrowserRouter key={option("key", options)}>
      <RouteForwarder routes={routes} onDone={onDone} options={options} key={option("key", options)} />
    </ReactRouterDom.BrowserRouter>
  })
}


interface RouteForwarderProps<R> {
  routes:Routes<R>
  onDone:Fun<R,void>
  options?:Options
}

interface RouteForwarderState<R> {
  readonly currentRoute?:Route<R>
  readonly currentRouteParams?:ReactRouterDom.RouteComponentProps<any>
  readonly callback?:() => void
}

class RouteForwarder<R> extends React.Component<RouteForwarderProps<R>, RouteForwarderState<R>> {
  constructor(props:RouteForwarderProps<R>, context:any) {
    super(props, context)

    this.state = {}
  }

  render() {
    return <ReactRouterDom.Switch key={option("key", this.props.options)}>
      {
        this.props.routes.map(currentRoute =>
          <ReactRouterDom.Route key={`route-${currentRoute.path}`} exact path={currentRoute.path}
            component={(currentRouteParams:ReactRouterDom.RouteComponentProps<any>) => {
                const s = this.state
                let nextState:RouteForwarderState<R> = ({...s,
                  currentRoute:currentRoute,
                  currentRouteParams:currentRouteParams,
                  callback:undefined
                })

                if ((this.state.currentRoute && this.state.currentRouteParams &&
                  (this.state.currentRouteParams.location.pathname != currentRouteParams.location.pathname ||
                   this.state.currentRouteParams.location.search != currentRouteParams.location.search)) ||
                  !this.state.currentRoute || !this.state.currentRouteParams) {
                  nextState = {...nextState, callback:() => this.props.onDone(
                    currentRoute.paramsToRoute({
                      ...currentRouteParams.match.params,
                      ...queryString.parse(currentRouteParams.location.search)}))
                    }
                  }

                if (nextState.callback != undefined) {
                  this.setState(nextState, () => {
                    if (this.state.callback) {

                      const callback = this.state.callback
                      return this.setState(s => ({...s, callback:undefined}), () => {
                        if (currentRoute.title != undefined) document.title = currentRoute.title
                        callback()
                      })
                    }
                  })
                }
                return null
              }
            } />
        )
      }
    </ReactRouterDom.Switch>
  }
}



// WEBPACK FOOTER //
// ./node_modules/widgets-for-react/src/primitives/router.tsx